import type { ForcedSubject, PureAbility } from '@casl/ability';

import type * as Abstract from './abstract';
import {
	makePermissionDbHelpers,
	makePermissionHelpers,
	makePermissionSystem,
} from './abstract';

const permissionTypes = [
	'Facebook/Media',
	'Facebook/Templates',
	'Facebook/Pages',
	'GoogleAds/Templates',
	'GMB/Templates',
	'GMB/Media',
	'Pacing',
	'Role',
	'User',
	'Salesforce Import',
	'Account Sync',
] as const;

const actionTypes = [
	'list',
	'view',
	'create',
	'update',
	'archive',
	'upload',
] as const;

type MetadataRow = Abstract.PermissionsMetadataDef<
	permissionSystem.PermissionType,
	permissionSystem.ActionType
>[permissionSystem.PermissionType];

/**
 * The permission meta data for most permission types.
 */
const standard: MetadataRow = {
	list: {},
	view: { needs: 'list' },
	create: { needs: 'view' },
	update: { needs: 'view' },
	archive: { needs: 'update' },
	upload: 'unused',
} as const;

const onlyList: MetadataRow = {
	list: {},
	view: 'unused',
	create: 'unused',
	update: 'unused',
	archive: 'unused',
	upload: 'unused',
};

const onlyCreate: MetadataRow = {
	list: 'unused',
	view: 'unused',
	create: {},
	update: 'unused',
	archive: 'unused',
	upload: 'unused',
};

const standardWithUpload: MetadataRow = {
	...standard,
	upload: { needs: 'view' },
} as const;

const system = makePermissionSystem({
	permissionTypes,
	actionTypes,

	superPerms: [
		['User', 'create'],
		['User', 'update'],
		['User', 'archive'],
	] as const,

	permissionsMetadata: {
		'Facebook/Media': standard,
		'Facebook/Templates': standardWithUpload,
		'Facebook/Pages': onlyList,
		'GoogleAds/Templates': standardWithUpload,
		'GMB/Templates': standardWithUpload,
		'GMB/Media': standardWithUpload,
		Pacing: onlyList,
		Role: standard,
		User: standard,
		'Salesforce Import': onlyCreate,
		'Account Sync': onlyCreate,
	},
});

const helpers = makePermissionHelpers(system);

const dbHelpers = makePermissionDbHelpers(system, {
	'Facebook/Media': 3,
	'Facebook/Templates': 4,
	'Facebook/Pages': 5,
	'GoogleAds/Templates': 6,
	'GMB/Templates': 8,
	'GMB/Media': 9,
	Pacing: 11,
	Role: 2,
	User: 1,
	'Salesforce Import': 7,
	'Account Sync': 10,
});

// For convenience, flatten all of these things into one object.
export const permissionSystem = {
	...system,
	...helpers,
	...dbHelpers,
};

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace permissionSystem {
	export type PermissionType = (typeof permissionTypes)[number];
	export type ActionType = (typeof actionTypes)[number];

	export type PermissionKey = readonly [PermissionType, ActionType];

	export type PermissionTable = Abstract.PermissionTable<
		PermissionType,
		ActionType
	>;

	export type Ability = PureAbility<
		[ActionType, PermissionType | ({ id: number } & ForcedSubject<'User'>)]
	>;
}
